import { FC, useContext } from 'react';

import { H3, NumberInput, Select, TTranslator } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, UseFormGetValues } from 'react-hook-form';

import { LocalizationContext, TLocalesKeys } from 'localization';
import { INormalizedTariff } from 'types';
import { getSeconds } from 'utils';

import s from '../Tariff.module.scss';
import { formatDuration } from '../utils';
import { ITariffLifetimeSectionProps } from './interfaces';

export const TariffLifetimeSection: FC<ITariffLifetimeSectionProps> = ({
  control,
  getValues,
}) => {
  const { t } = useContext(LocalizationContext);

  const validateLifetime = (
    val: '' | number,
    getValues: UseFormGetValues<INormalizedTariff>,
    t: TTranslator<TLocalesKeys>
  ): string | undefined => {
    if (val === '') {
      return t('errors.required');
    }

    const lifetimePeriod = getValues('lifetime_type');
    const seconds = getSeconds(val || 0, lifetimePeriod as string) ?? 0;

    if (seconds / 60 < 1) {
      return t('errors.min', 1, t('time.contracted.minutes'));
    }

    if (seconds > 2592000) {
      return t('errors.max', 30, t('time.contracted.days'));
    }

    const duration = getValues('duration');
    const durationPeriod = getValues('duration_type');
    const durationSeconds = getSeconds(duration || 0, durationPeriod) ?? 0;

    if (durationSeconds > seconds) {
      return t('errors.min', duration, formatDuration(durationPeriod, t));
    }

    return undefined;
  };

  return (
    <div className={cn(s['form-block'], s['lifetime-block'])}>
      <H3>{t('tariff.lifeTime')}</H3>
      <div className={s.fields}>
        <Controller
          control={control}
          render={({ field, fieldState: { error } }) => (
            <NumberInput
              {...field}
              title={t('duration')}
              placeholder="1"
              error={error?.message}
              errorWrapperClassName={s.duration}
              bordered
            />
          )}
          name="lifetime"
          rules={{
            validate: (val) => validateLifetime(val, getValues, t),
          }}
        />
        <Controller
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Select
              {...field}
              title={t('tariff.format')}
              bordered
              options={[
                {
                  title: t('tariff.format.minutes'),
                  value: 'minutes',
                },
                {
                  title: t('tariff.format.hours'),
                  value: 'hours',
                },
                {
                  title: t('tariff.format.days'),
                  value: 'days',
                },
                {
                  title: t('tariff.format.weeks'),
                  value: 'weeks',
                },
              ]}
              disableClear
            />
          )}
          name="lifetime_type"
        />
      </div>
    </div>
  );
};
